import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * Room Header - Displays room name and "VIEW ALL" button
 *
 * Shows the current game room name (e.g., "eu-ffa") with a button
 * to open the full scoreboard panel.
 *
 * Desktop only — on mobile the mini-leaderboard it captions is hidden, so
 * this would float orphaned in the top-right. The scoreboard is reached
 * instead via the podium button in menu-toolbar; the room name is already
 * shown in the top-left game-selector chip.
 *
 * @element room-header
 */
@customElement('room-header')
export class RoomHeader extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        /* Legacy's 20px margin plus the space between its two inline blocks. */
        gap: 26px;
        position: absolute;
        top: 210px;
        right: 10px;
        width: 258px;
        /* Declared here rather than inherited from the body. */
        font-family: var(--font-family);
        font-size: var(--font-size-xl);
        cursor: default;
        padding: 10px 0;
        pointer-events: none;
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .room-name {
        color: var(--color-text-white);
        font-weight: bold;
        /* Legacy's softer glow; no token matches. */
        text-shadow: 0 0 6px rgba(0, 0, 0, 0.6);
      }

      /* A <button> doesn't inherit font-family (the UA gives it Arial), and it
         keeps focus after a click, so the next key you fly with lit up its
         focus ring. The same reset as the toolbar chips. */
      .view-all {
        font-family: var(--font-family);
        outline: none;
        padding: 4px 10px;
        background-color: var(--color-btn-play);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-13);
        font-weight: bold;
        cursor: pointer;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text-btn);
        border: none;
        border-bottom: 2px solid var(--color-border-soft);
        pointer-events: auto;
      }

      /* Legacy's half-black shadow, lighter than the play button's
         (--shadow-btn-hover); no token matches. */
      @media (hover: hover) {
        .view-all:hover {
          background-color: var(--color-btn-play-hover);
          box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
        }
      }

      .view-all:active {
        border-bottom: none;
        border-top: 2px solid var(--color-border-strong);
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  /** Handle VIEW ALL button click */
  private handleViewAllClick = (e: Event) => {
    e.stopPropagation();
    // Float cleanup (top-bar popovers, context menu) is centralized in
    // closeAllPanels(), which showScorePanel() runs — no manual close needed.
    this.api.playClick();
    this.api.showScorePanel();
  };

  override render() {
    // Mobile: the mini-leaderboard this captions is hidden, so the lone
    // room name + VIEW ALL button would float orphaned. Scoreboard access
    // moves to the menu-toolbar podium button.
    if (this.uiState.isMobile) return nothing;
    // F3 hides the room-header alongside the mini-leaderboard — they're a
    // visual unit (room name + VIEW ALL button sits directly above the
    // leaderboard list) so toggling one without the other looks broken.
    if (this.uiState.leaderboardHidden) return nothing;
    const gameName = this.uiState.currentGameName;
    if (!gameName) return nothing;

    return html`
      <span class="room-name">${gameName}</span>
      <button class="view-all" @click=${this.handleViewAllClick}>VIEW ALL</button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'room-header': RoomHeader;
  }
}
